import React, { useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { TransformWrapper, TransformComponent, useControls } from 'react-zoom-pan-pinch';
import { useSwipeable } from 'react-swipeable';
import FigureLightbox from './FigureLightbox';

// ─── Zoom controls toolbar ────────────────────────────────────────────────────

function ZoomControls() {
  const { zoomIn, zoomOut, resetTransform } = useControls();
  return (
    <div className="flex items-center gap-1">
      <button
        onClick={() => zoomIn()}
        title="Zoom in"
        className="w-7 h-7 flex items-center justify-center rounded bg-[#21262d] border border-[#30363d] text-[#8b949e] hover:text-[#e6edf3] hover:border-[#58a6ff] transition-colors text-sm font-mono"
      >
        +
      </button>
      <button
        onClick={() => zoomOut()}
        title="Zoom out"
        className="w-7 h-7 flex items-center justify-center rounded bg-[#21262d] border border-[#30363d] text-[#8b949e] hover:text-[#e6edf3] hover:border-[#58a6ff] transition-colors text-sm font-mono"
      >
        −
      </button>
      <button
        onClick={() => resetTransform()}
        title="Reset view"
        className="w-7 h-7 flex items-center justify-center rounded bg-[#21262d] border border-[#30363d] text-[#8b949e] hover:text-[#e6edf3] hover:border-[#58a6ff] transition-colors"
      >
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
      </button>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function ResourceViewer({ resource, revealCaption = true }) {
  const [activeFigIndex, setActiveFigIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen]     = useState(false);
  const [lightboxIndex, setLightboxIndex]   = useState(0);

  // Read current zoom scale before deciding whether a swipe should change
  // figures or be left for the pan-zoom layer.
  const transformRef = useRef(null);

  const handleTabChange = useCallback((i) => {
    setActiveFigIndex(i);
  }, []);

  const handleImageClick = useCallback((index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  }, []);

  const figures = resource?.figures ?? [];
  const hasMultiple = figures.length > 1;

  // Swipe handlers — only navigate when not zoomed in (so the user can still
  // pan a zoomed image without accidentally swapping figures). Hook order
  // must stay stable across renders, so register before any early return.
  const swipeHandlers = useSwipeable({
    onSwipedLeft:  () => {
      if (hasMultiple && (transformRef.current?.state.scale ?? 1) <= 1) {
        setActiveFigIndex((i) => Math.min(figures.length - 1, i + 1));
      }
    },
    onSwipedRight: () => {
      if (hasMultiple && (transformRef.current?.state.scale ?? 1) <= 1) {
        setActiveFigIndex((i) => Math.max(0, i - 1));
      }
    },
    delta: 50,
    preventScrollOnSwipe: true,
    trackTouch: true,
    trackMouse: false,
  });

  if (!figures.length) return null;

  const activeFig = figures[activeFigIndex];
  const goNext = () => setActiveFigIndex((i) => Math.min(figures.length - 1, i + 1));
  const goPrev = () => setActiveFigIndex((i) => Math.max(0, i - 1));

  return (
    <div className="h-full flex flex-col bg-[#161b22] border-l border-[#30363d] transition-opacity duration-300">

      {/* ── Header ─────────────────────────────────────────────────── */}
      <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 border-b border-[#30363d] bg-[#0d1117]">
        <div className="flex items-center gap-2">
          <svg className="w-4 h-4 text-[#58a6ff]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <span className="text-xs font-mono font-semibold text-[#e6edf3] tracking-wide uppercase">
            Resource
          </span>
        </div>
        <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-[#58a6ff]/10 text-[#58a6ff] border border-[#58a6ff]/20">
          {figures.length} {figures.length === 1 ? 'figure' : 'figures'}
        </span>
      </div>

      {/* ── Figure tabs (multi-figure only) ────────────────────────── */}
      {hasMultiple && (
        <div className="flex-shrink-0 flex border-b border-[#30363d] bg-[#0d1117]">
          {figures.map((fig, i) => {
            const isCard = fig.is_question_card;
            return (
              <button
                key={fig.fig_id}
                onClick={() => handleTabChange(i)}
                className={`flex-1 py-2 text-xs font-mono transition-colors ${
                  i === activeFigIndex
                    ? 'text-[#58a6ff] bg-[#58a6ff]/10 border-b-2 border-[#58a6ff]'
                    : 'text-[#8b949e] hover:text-[#e6edf3] hover:bg-[#161b22]'
                }`}
              >
                {isCard ? 'Question' : `Fig ${fig.fig_id}`}
              </button>
            );
          })}
        </div>
      )}

      {/* ── Image viewer ───────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col min-h-0 overflow-hidden" {...swipeHandlers}>
        <TransformWrapper
          key={activeFig.fig_id}
          ref={transformRef}
          initialScale={1}
          minScale={0.5}
          maxScale={8}
          centerOnInit
          wheel={{ step: 0.1 }}
          doubleClick={{ mode: 'toggle' }}
        >
          {/* Controls bar */}
          <div className="flex-shrink-0 flex items-center justify-between px-3 py-2 bg-[#0d1117] border-b border-[#30363d]">
            <span className="text-[10px] font-mono text-[#8b949e]">
              {hasMultiple
                ? 'Swipe or use › for related figures'
                : 'Scroll to zoom · Drag to pan · Double-click to reset'}
            </span>
            <div className="flex items-center gap-1">
              {/* Expand / fullscreen trigger */}
              <button
                onClick={() => handleImageClick(activeFigIndex)}
                title="View fullscreen"
                aria-label="View figure fullscreen"
                className="w-7 h-7 flex items-center justify-center rounded bg-[#21262d] border border-[#30363d] text-[#8b949e] hover:text-[#e6edf3] hover:border-[#58a6ff] transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5" />
                </svg>
              </button>
              <ZoomControls />
            </div>
          </div>

          {/* Image (relative wrapper so we can overlay chevrons) */}
          <div className="relative flex-1 min-h-0 flex flex-col">
            <TransformComponent
              wrapperStyle={{ width: '100%', flex: 1, overflow: 'hidden' }}
              contentStyle={{ width: '100%', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '12px' }}
            >
              <img
                src={activeFig.file_path}
                alt={activeFig.alt_text}
                className="max-w-full rounded border border-[#30363d] shadow-lg"
                draggable={false}
              />
            </TransformComponent>

            {hasMultiple && (
              <>
                {/* Prev chevron — hidden on the first figure */}
                {activeFigIndex > 0 && (
                  <button
                    onClick={goPrev}
                    aria-label="Previous figure"
                    title="Previous figure"
                    className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-full bg-[#161b22]/85 border border-[#30363d] text-[#8b949e] hover:text-[#e6edf3] hover:border-[#58a6ff] transition-colors backdrop-blur-sm"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                )}
                {/* Next chevron — hidden on the last figure */}
                {activeFigIndex < figures.length - 1 && (
                  <button
                    onClick={goNext}
                    aria-label="Next figure"
                    title="Next figure"
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-full bg-[#161b22]/85 border border-[#30363d] text-[#8b949e] hover:text-[#e6edf3] hover:border-[#58a6ff] transition-colors backdrop-blur-sm"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                )}
                {/* Dot indicator */}
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1.5 px-2 py-1 rounded-full bg-[#161b22]/85 border border-[#30363d] backdrop-blur-sm">
                  {figures.map((fig, i) => (
                    <button
                      key={fig.fig_id}
                      onClick={() => handleTabChange(i)}
                      aria-label={`Go to figure ${i + 1}`}
                      className={`w-1.5 h-1.5 rounded-full transition-colors ${
                        i === activeFigIndex ? 'bg-[#58a6ff]' : 'bg-[#30363d] hover:bg-[#484f58]'
                      }`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </TransformWrapper>
      </div>

      {/* ── Caption ────────────────────────────────────────────────── */}
      {/* Skip the caption block entirely for question-card screenshots
          (the image already contains the question and any embedded figure
          caption, so there's nothing to "describe" or "reveal"). Also skip
          when the description is empty in reveal mode. */}
      {!activeFig.is_question_card && (revealCaption ? !!activeFig.description : true) && (
        <div className="flex-shrink-0 px-4 py-3 border-t border-[#30363d] bg-[#0d1117]">
          {revealCaption ? (
            <p className="text-[11px] font-mono text-[#8b949e] leading-relaxed">
              {activeFig.description}
            </p>
          ) : (
            <p className="text-[11px] font-mono text-[#484f58] italic leading-relaxed">
              Figure description is hidden during the exam and will be revealed once your answer has been marked.
            </p>
          )}
        </div>
      )}

      {/* ── Lightbox portal ────────────────────────────────────────── */}
      {lightboxOpen && createPortal(
        <FigureLightbox
          figures={figures}
          initialIndex={lightboxIndex}
          revealCaption={revealCaption}
          onClose={() => setLightboxOpen(false)}
        />,
        document.body,
      )}
    </div>
  );
}
